import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { UserProfile, AuthResponse } from '@spi/shared';
import { setAccessToken } from '../api/client';
import * as authApi from '../api/auth';
import { PersonaProvider, usePersona } from './PersonaContext';

interface AuthContextValue {
  /** The effective user (impersonated if in persona mode, otherwise real user) */
  user: UserProfile | null;
  /** The real admin user (always the logged-in user, never impersonated) */
  realUser: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: UserProfile | null) => void;
  updateSession: (response: AuthResponse) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function AuthContextInner({ children, realUser, isLoading, login, logout, setUser, updateSession }: {
  children: ReactNode;
  realUser: UserProfile | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: UserProfile | null) => void;
  updateSession: (response: AuthResponse) => void;
}) {
  const { persona, isImpersonating } = usePersona();

  const effectiveUser = isImpersonating && persona ? persona : realUser;

  return (
    <AuthContext.Provider
      value={{
        user: effectiveUser,
        realUser,
        isLoading,
        isAuthenticated: !!realUser,
        login,
        logout,
        setUser,
        updateSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [realUser, setRealUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    authApi.refreshSession().then((result) => {
      if (result) {
        setRealUser(result.user);
      }
      setIsLoading(false);
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login({ email, password });
    setRealUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setRealUser(null);
  }, []);

  const updateSession = useCallback((response: AuthResponse) => {
    setAccessToken(response.accessToken);
    setRealUser(response.user);
  }, []);

  return (
    <PersonaProvider realUser={realUser}>
      <AuthContextInner
        realUser={realUser}
        isLoading={isLoading}
        login={login}
        logout={logout}
        setUser={setRealUser}
        updateSession={updateSession}
      >
        {children}
      </AuthContextInner>
    </PersonaProvider>
  );
}
